import { push } from 'connected-react-router';
import produce from 'immer';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { oc } from 'ts-optchain';
import { SettingsApi } from 'vrlcm-gui-api';
import {
  EnvironmentRequest,
  EnvironmentRequestProduct,
  EnvironmentState,
  SelectedProduct,
  UIData,
  VcfInfraProperties,
  VcfModeSettings
} from 'vrlcm-gui-types';
import { safeJsonParse, toSafeUIData } from 'vrlcm-gui-utils';
import {
  patchEnvironmentState,
  patchEnvironmentStateFailed
} from '../../../actions/workflows/EnvironmentWorkflow';
import {
  initializeStepEnvironmentFailed,
  initializeStepEnvironmentSuccess,
  SubmitStepEnvironment
} from '../../../actions/workflows/steps/Environment';
import EnvironmentsAPI from '../../../api/Environments';
import EnvironmentStepAPI from '../../../api/workflows/steps/Environment';
import * as Constants from '../../../constants/action-constants';
import { ROUTES } from '../../../constants/RouteConstants';
import { ROUTES as CLOUD_ROUTES } from '../../../../cloud/constants/RouteConstants';
import { StoreState } from '../../../types';
import { ValidateSpecResponse } from '../../../types/workflows/steps/Environment';

function* initializeStepEnvironment() {
  try {
    const environments = yield call(EnvironmentsAPI.getEnvironments);
    yield put(initializeStepEnvironmentSuccess(environments));
  } catch (e) {
    yield put(initializeStepEnvironmentFailed());
  }
}

function* watchForEnvironment() {
  yield takeLatest(
    Constants.INITIALIZE_STEP_ENVIRONMENT,
    initializeStepEnvironment
  );
}

function* submitStepEnvironment(action: SubmitStepEnvironment) {
  try {
    let inputState = action.payload.state;
    const inputUiData = toSafeUIData(inputState.uiData);
    const isCloudProxyEnvironment = inputUiData.isCloudProxyEnvironment;
    let vcfProperties = safeJsonParse<VcfInfraProperties>(
      inputState.environmentRequestDTO.infrastructure.properties.vcfProperties
    );
    if (vcfProperties && vcfProperties.vcfEnabled) {
      const { vcfHostname }: VcfModeSettings = yield call(
        SettingsApi.getVcfModeSettings
      );
      vcfProperties.sddcManagerDetails = [
        {
          sddcManagerHostName: vcfHostname,
          sddcManagerName: 'default',
          sddcManagerVmid: 'default'
        }
      ];
      inputState = produce<EnvironmentState, EnvironmentState>(
        inputState,
        draft => {
          draft.environmentRequestDTO.infrastructure.properties.vcfProperties = JSON.stringify(
            vcfProperties
          );
        }
      );
    }
    let finalState = inputState;
    if (inputUiData.configuration) {
      const configurationData = safeJsonParse<EnvironmentRequest>(
        inputUiData.configuration
      );
      if (configurationData) {
        configurationData.environmentId =
          inputState.environmentRequestDTO.environmentId;
        configurationData.environmentName =
          inputState.environmentRequestDTO.environmentName;
        configurationData.infrastructure.properties.dataCenterVmid =
          inputState.environmentRequestDTO.infrastructure.properties.dataCenterVmid;
        const validationResponse: ValidateSpecResponse[] = yield call(
          EnvironmentStepAPI.validateJsonSpec,
          configurationData
        );
        if (validationResponse.some(i => i.status === 'FAILED')) {
          const errors = validationResponse
            .filter(i => i.status === 'FAILED')
            .map(i => i.resultDescription);
          const errorString = errors.join();
          throw Error(errorString);
        }
        let lockerReferenceState: EnvironmentRequest;
        try {
          lockerReferenceState = yield call(
            EnvironmentStepAPI.createLockerReferenceFromJsonSpec,
            configurationData
          );
          lockerReferenceState.products = lockerReferenceState.products.map(
            p => {
              if (p.id === 'vra') p.properties.__vraTenantList = '[]';
              return p;
            }
          );
        } catch (e) {
          throw Error(e.response.data.message);
        }
        finalState = produce<EnvironmentState, EnvironmentState>(
          inputState,
          draft => {
            const infraProps =
              inputState.environmentRequestDTO.infrastructure.properties;
            const configurationProps = oc(
              lockerReferenceState
            ).infrastructure.properties(infraProps);
            const products: EnvironmentRequestProduct[] = oc(
              lockerReferenceState
            ).products(inputState.environmentRequestDTO.products);
            const selectedProducts: SelectedProduct[] = products.map<
              SelectedProduct
            >(p => ({
              id: p.id,
              selectedDeploymentType: 'config',
              selectedAuthenticationType: 'config',
              selectedVersion: p.version,
              selected: true,
              type: p.properties.isImport === 'true' ? 'import' : 'new',
              sizes: {},
              description: '',
              detailsHref: '',
              errorMessage: '',
              productVersions: []
            }));

            const finalUiData: UIData = {
              ...inputUiData,
              configuration: JSON.stringify(lockerReferenceState, null, 2)
            };

            draft.uiData = JSON.stringify(finalUiData);

            draft.environmentRequestDTO = {
              environmentId: inputState.environmentRequestDTO.environmentId,
              environmentName: inputState.environmentRequestDTO.environmentName,
              infrastructure: {
                properties: {
                  ...configurationProps,
                  dataCenterVmid: infraProps.dataCenterVmid,
                  regionName: infraProps.regionName,
                  zoneName: infraProps.zoneName,
                  enableTelemetry: infraProps.enableTelemetry,
                  defaultPassword: infraProps.defaultPassword,
                  _selectedProducts: JSON.stringify(selectedProducts)
                }
              },
              products: products.map(p => {
                if (p.id === 'vra') p.properties.__vraTenantList = '[]';
                return p;
              })
            };
            draft.environmentDescription = inputState.environmentDescription;
          }
        );
      }
    }
    yield put(patchEnvironmentState(finalState));
    const requestId = yield select(
      (state: StoreState) => state.environmentWorkflow.state.requestId
    );
    if (isCloudProxyEnvironment) {
      yield put(push(`${CLOUD_ROUTES.CLOUD_PROXY}/${requestId}`));
    } else {
      yield put(push(`${ROUTES.ENVIRONMENT_WORKFLOW}/${requestId}`));
    }
  } catch (e) {
    yield put(patchEnvironmentStateFailed(e.message));
  }
}

function* watchForSubmitEnvironment() {
  yield takeLatest(Constants.SUBMIT_STEP_ENVIRONMENT, submitStepEnvironment);
}

export default function* environmentSaga() {
  yield all([watchForEnvironment(), watchForSubmitEnvironment()]);
}
